import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Pressable,
  TextInput,
  ScrollView,
  Platform,
  Modal,
  Dimensions,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import { BrandColors, MaterialShapes, MaterialElevations, Fonts } from "@/constants/ThemeColours";

/**
 * Interface representing an individual selectable option in ThemedDropDown.
 */
export interface IDropdownOption<T = string> {
  /** Human-readable display label */
  label: string;
  /** Underlying value representation */
  value: T;
  /** Optional icon displayed alongside the label */
  icon?: keyof typeof Ionicons.glyphMap;
  /** Optional custom icon tint color */
  iconColor?: string;
  /** Optional secondary descriptive note */
  description?: string;
  /** Whether this specific option is disabled from selection */
  disabled?: boolean;
}

/**
 * Properties for the standardised ThemedDropDown component.
 */
export interface IThemedDropDownProps<T = string> {
  /** Array of available options */
  options: IDropdownOption<T>[];
  /** Currently selected value */
  value?: T;
  /** Callback fired when an option is selected */
  onSelect: (option: IDropdownOption<T>) => void;
  /** Placeholder displayed when no option is selected */
  placeholder?: string;
  /** Optional field header label */
  label?: string;
  /** Optional helper text displayed beneath the trigger */
  helperText?: string;
  /** Optional validation error message */
  error?: string | null;
  /** Whether the dropdown is locked in read-only mode */
  readOnly?: boolean;
  /** Whether the dropdown is completely disabled */
  disabled?: boolean;
  /** Whether to enable live text search filtering inside the menu (Combobox mode) */
  searchable?: boolean;
  /** Whether to render in compact density mode (for toolbars and inline panels) */
  compact?: boolean;
  /** Optional leading icon displayed inside the trigger box */
  leadingIcon?: keyof typeof Ionicons.glyphMap;
  /** Optional color for the leading icon */
  leadingIconColor?: string;
  /** Outer container style */
  containerStyle?: StyleProp<ViewStyle>;
  /** Trigger box style */
  triggerStyle?: StyleProp<ViewStyle>;
  /** Menu popout style */
  menuStyle?: StyleProp<ViewStyle>;
  /** Maximum height for the option scroll view */
  maxMenuHeight?: number;
  /** Accessibility label */
  accessibilityLabel?: string;
  /** Test identifier */
  testID?: string;
}

/**
 * Calculates high-contrast foreground color (light vs dark) based on background color.
 * Adheres to WCAG AAA relative luminance standards.
 *
 * @param {string} bg - The background hex or rgb string.
 * @returns {{ textColor: string; descColor: string }} Contrasting text and description colors.
 */
export function getContrastingTextColor(bg: string): { textColor: string; descColor: string } {
  if (!bg) {
    return { textColor: "#1D1B20", descColor: "rgba(29, 27, 32, 0.75)" };
  }

  let r = 0, g = 0, b = 0;
  if (bg.startsWith("#")) {
    const hex = bg.replace("#", "");
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length >= 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }
  } else if (bg.startsWith("rgb")) {
    const match = bg.match(/\d+/g);
    if (match && match.length >= 3) {
      r = parseInt(match[0], 10);
      g = parseInt(match[1], 10);
      b = parseInt(match[2], 10);
    }
  }

  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };

  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  const isLight = luminance >= 0.4;

  return {
    textColor: isLight ? "#1D1B20" : "#FFFFFF",
    descColor: isLight ? "rgba(29, 27, 32, 0.75)" : "rgba(255, 255, 255, 0.85)",
  };
}

/**
 * Standardised Archival Heirloom Dropdown & Combobox component.
 * Adheres to 4px architectural border curvature, Butter Bean parchment surfaces,
 * compact density rhythm, and strict Purple Ban rules.
 *
 * @param {IThemedDropDownProps<T>} props - Dropdown properties.
 * @returns {React.JSX.Element} The rendered dropdown component.
 */
export function ThemedDropDown<T = string>({
  options,
  value,
  onSelect,
  placeholder = "Select an option...",
  label,
  helperText,
  error,
  readOnly = false,
  disabled = false,
  searchable = false,
  compact = false,
  leadingIcon,
  leadingIconColor,
  containerStyle,
  triggerStyle,
  menuStyle,
  maxMenuHeight = 220,
  accessibilityLabel,
  testID,
}: IThemedDropDownProps<T>): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isTriggerHovered, setIsTriggerHovered] = useState(false);
  const triggerRef = useRef<View>(null);
  const [menuLayout, setMenuLayout] = useState<{
    top: number;
    left: number;
    width: number;
  }>({ top: 0, left: 0, width: 200 });

  const { popupMenu, inputForm } = useComponentTheme();
  const bgColor = popupMenu.card.backgroundColor;
  const bgSelected = popupMenu.item.selectedBg;
  const borderColor = popupMenu.card.borderColor;
  const textColor = popupMenu.item.textColor;
  const inputTextColor = inputForm.input.textColor;
  const textMuted = popupMenu.item.mutedTextColor;
  const tintColor = popupMenu.item.accentBarColor;
  const hoverBorderColor = inputForm.input.hoverBorder;
  const inputReadOnlyBg = inputForm.input.readOnlyBg;
  const inputReadOnlyBorder = inputForm.input.readOnlyBorder;
  const inputReadOnlyText = inputForm.input.readOnlyTextColor;

  const selectedOption = useMemo(() => {
    return options.find((opt) => opt.value === value);
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) {
      return options;
    }
    const query = searchQuery.trim().toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        (opt.description && opt.description.toLowerCase().includes(query)),
    );
  }, [options, searchable, searchQuery]);

  const handleToggle = (): void => {
    if (readOnly || disabled) return;
    if (isOpen) {
      setIsOpen(false);
      setHoveredIndex(null);
      return;
    }

    const currentSelectedIdx = options.findIndex((opt) => opt.value === value);
    const initialHoverIdx = currentSelectedIdx >= 0 ? currentSelectedIdx : 0;

    if (triggerRef.current) {
      if (Platform.OS === "web") {
        const node = triggerRef.current as any;
        if (typeof node.getBoundingClientRect === "function") {
          const rect = node.getBoundingClientRect();
          const screenHeight = Dimensions.get("window").height;
          const screenWidth = Dimensions.get("window").width;
          const estimatedMenuHeight = Math.min(maxMenuHeight + (searchable ? 44 : 0), 260);
          const opensUpward =
            rect.bottom + estimatedMenuHeight > screenHeight && rect.top > estimatedMenuHeight;
          const menuWidth = Math.min(Math.max(rect.width, 180), Math.max(120, screenWidth - 16));
          const clampedLeft = Math.min(Math.max(8, rect.left), Math.max(8, screenWidth - menuWidth - 8));
          setMenuLayout({
            top: opensUpward ? Math.max(8, rect.top - estimatedMenuHeight - 4) : rect.bottom + 4,
            left: clampedLeft,
            width: menuWidth,
          });
          setHoveredIndex(initialHoverIdx);
          setIsOpen(true);
          setSearchQuery("");
          return;
        }
      }

      triggerRef.current.measureInWindow((x, y, width, height) => {
        const screenHeight = Dimensions.get("window").height;
        const screenWidth = Dimensions.get("window").width;
        const estimatedMenuHeight = Math.min(maxMenuHeight + (searchable ? 44 : 0), 260);
        const opensUpward =
          y + height + estimatedMenuHeight > screenHeight && y > estimatedMenuHeight;
        const menuWidth = Math.min(Math.max(width, 180), Math.max(120, screenWidth - 16));
        const clampedLeft = Math.min(Math.max(8, x), Math.max(8, screenWidth - menuWidth - 8));
        setMenuLayout({
          top: opensUpward ? Math.max(8, y - estimatedMenuHeight - 4) : y + height + 4,
          left: clampedLeft,
          width: menuWidth,
        });
        setHoveredIndex(initialHoverIdx);
        setIsOpen(true);
        setSearchQuery("");
      });
    } else {
      setHoveredIndex(initialHoverIdx);
      setIsOpen(true);
      setSearchQuery("");
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (Platform.OS === "web" && typeof window !== "undefined") {
      const handleScrollOrResize = () => {
        setIsOpen(false);
        setHoveredIndex(null);
      };
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
      return () => {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    }
    return undefined;
  }, [isOpen]);

  const handleSelect = (option: IDropdownOption<T>): void => {
    if (option.disabled) return;
    onSelect(option);
    setIsOpen(false);
    setHoveredIndex(null);
    setSearchQuery("");
  };

  const hasError = Boolean(error && error.trim().length > 0);
  const triggerBg = readOnly ? inputReadOnlyBg : bgColor;
  const triggerBorder = readOnly
    ? inputReadOnlyBorder
    : hasError
    ? BrandColors.beetRed.hex
    : isOpen
    ? tintColor
    : isTriggerHovered && !disabled
    ? hoverBorderColor
    : borderColor;

  return (
    <View style={[styles.wrapper, containerStyle]} testID={testID}>
      {label ? (
        <ThemedText type="stamp" style={[styles.fieldLabel, { color: textMuted }]}>
          {label}
        </ThemedText>
      ) : null}

      <View
        ref={triggerRef}
        collapsable={false}
        style={styles.anchorContainer}
      >
        {/* Trigger Button */}
        <TouchableOpacity
          activeOpacity={readOnly || disabled ? 1 : 0.8}
          onPress={handleToggle}
          {...(Platform.select({
            web: {
              onMouseEnter: () => !readOnly && !disabled && setIsTriggerHovered(true),
              onMouseLeave: () => setIsTriggerHovered(false),
            } as any,
            default: {},
          }))}
          style={[
            styles.triggerBase,
            compact ? styles.triggerCompact : styles.triggerStandard,
            {
              backgroundColor: triggerBg,
              borderColor: triggerBorder,
            },
            disabled && styles.triggerDisabled,
            triggerStyle,
          ]}
          accessibilityRole="combobox"
          accessibilityLabel={accessibilityLabel || label || placeholder}
          accessibilityState={{ expanded: isOpen, disabled: disabled || readOnly }}
        >
          <View style={styles.triggerContent}>
            {leadingIcon ? (
              <Ionicons
                name={leadingIcon}
                size={compact ? 14 : 16}
                color={leadingIconColor || (readOnly ? inputReadOnlyText : tintColor)}
                style={styles.leadingIcon}
              />
            ) : selectedOption?.icon ? (
              <Ionicons
                name={selectedOption.icon}
                size={compact ? 14 : 16}
                color={selectedOption.iconColor || tintColor}
                style={styles.leadingIcon}
              />
            ) : null}

            <ThemedText
              numberOfLines={1}
              style={[
                compact ? styles.valueTextCompact : styles.valueTextStandard,
                {
                  color: readOnly
                    ? inputReadOnlyText
                    : selectedOption
                    ? textColor
                    : textMuted,
                  fontStyle: !selectedOption && !readOnly ? "italic" : "normal",
                },
              ]}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </ThemedText>
          </View>

          {readOnly ? (
            <Ionicons name="lock-closed-outline" size={compact ? 13 : 15} color={inputReadOnlyText} />
          ) : (
            <Ionicons
              name={isOpen ? "chevron-up" : "chevron-down"}
              size={compact ? 13 : 15}
              color={isOpen ? tintColor : textMuted}
            />
          )}
        </TouchableOpacity>

        {/* Floating Menu Popout via Modal Portal */}
        {isOpen && !readOnly && !disabled && (
          <Modal
            transparent
            visible={isOpen}
            animationType="none"
            onRequestClose={() => setIsOpen(false)}
          >
            {/* Backdrop for outside dismissal */}
            <Pressable
              style={StyleSheet.absoluteFill}
              onPress={() => setIsOpen(false)}
              accessibilityLabel="Dismiss options menu"
            />

            <View
              style={[
                styles.menuCard,
                {
                  position: "absolute",
                  top: menuLayout.top,
                  left: menuLayout.left,
                  width: menuLayout.width,
                  backgroundColor: bgColor,
                  borderColor,
                },
                menuStyle,
              ]}
            >
              {/* Optional Combobox Search Filter Input */}
              {searchable && (
                <View style={[styles.searchRow, { borderBottomColor: borderColor }]}>
                  <Ionicons name="search-outline" size={14} color={textMuted} style={{ marginRight: 6 }} />
                  <TextInput
                    placeholder="Search options..."
                    placeholderTextColor={textMuted}
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                    style={[styles.searchInput, { color: inputTextColor }]}
                    autoFocus
                    autoCorrect={false}
                    autoCapitalize="none"
                  />
                  {searchQuery.length > 0 && (
                    <TouchableOpacity onPress={() => setSearchQuery("")} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                      <Ionicons name="close-circle" size={14} color={textMuted} />
                    </TouchableOpacity>
                  )}
                </View>
              )}

              {/* Scrollable List of Options */}
              <ScrollView
                style={{ maxHeight: maxMenuHeight }}
                keyboardShouldPersistTaps="handled"
                nestedScrollEnabled
              >
                {filteredOptions.length === 0 ? (
                  <View style={styles.emptyOptionRow}>
                    <ThemedText style={[styles.emptyOptionText, { color: textMuted }]}>
                      No matching options found
                    </ThemedText>
                  </View>
                ) : (
                  filteredOptions.map((opt, idx) => {
                    const isSelected = opt.value === value;
                    const isHovered = hoveredIndex === idx;

                    let itemBg = inputReadOnlyBg;
                    let itemBorder = inputReadOnlyBorder;
                    let labelColor = inputReadOnlyText;
                    let descColor = inputReadOnlyText;
                    let iconColor = opt.iconColor || inputReadOnlyText;
                    let checkmarkColor = "#1D1B20";

                    if (isSelected) {
                      itemBg = Platform.select({ android: bgSelected, default: tintColor });
                      const contrast = getContrastingTextColor(itemBg);
                      itemBorder = isHovered
                        ? (contrast.textColor === "#1D1B20" ? "#000000" : BrandColors.butterBean.hex)
                        : itemBg;
                      labelColor = contrast.textColor;
                      descColor = contrast.descColor;
                      iconColor = opt.iconColor || contrast.textColor;
                      checkmarkColor = contrast.textColor;
                    } else if (isHovered) {
                      itemBg = bgSelected;
                      itemBorder = tintColor;
                      labelColor = textColor;
                      descColor = textMuted;
                      iconColor = opt.iconColor || tintColor;
                    }

                    return (
                      <Pressable
                        key={String(opt.value ?? idx)}
                        disabled={opt.disabled}
                        onPress={() => handleSelect(opt)}
                        onHoverIn={() => setHoveredIndex(idx)}
                        onHoverOut={() => setHoveredIndex((prev) => (prev === idx ? null : prev))}
                        {...Platform.select({
                          web: {
                            onMouseEnter: () => setHoveredIndex(idx),
                            onMouseLeave: () => setHoveredIndex((prev) => (prev === idx ? null : prev)),
                            onPointerEnter: () => setHoveredIndex(idx),
                            onPointerLeave: () => setHoveredIndex((prev) => (prev === idx ? null : prev)),
                          } as any,
                          default: {},
                        })}
                        style={({ pressed }) => [
                          styles.optionItem,
                          compact && styles.optionItemCompact,
                          {
                            backgroundColor: pressed ? bgSelected : itemBg,
                            borderColor: itemBorder,
                          },
                          isHovered && styles.optionItemHovered,
                          opt.disabled && { opacity: 0.4 },
                        ]}
                        accessibilityRole="button"
                        accessibilityState={{ selected: isSelected, disabled: opt.disabled }}
                      >
                        <View style={styles.optionLabelContainer}>
                          {opt.icon && (
                            <Ionicons
                              name={opt.icon}
                              size={compact ? 14 : 16}
                              color={iconColor}
                              style={{ marginRight: 8 }}
                            />
                          )}
                          <View style={{ flex: 1, gap: 1 }}>
                            <ThemedText
                              style={[
                                styles.optionLabel,
                                compact && styles.optionLabelCompact,
                                {
                                  color: labelColor,
                                  fontWeight: isSelected || isHovered ? "700" : "500",
                                },
                              ]}
                            >
                              {opt.label}
                            </ThemedText>
                            {opt.description && (
                              <ThemedText
                                style={[
                                  styles.optionDesc,
                                  { color: descColor },
                                ]}
                              >
                                {opt.description}
                              </ThemedText>
                            )}
                          </View>
                        </View>

                        {isSelected && (
                          <Ionicons name="checkmark" size={compact ? 14 : 16} color={checkmarkColor} />
                        )}
                      </Pressable>
                    );
                  })
                )}
              </ScrollView>
            </View>
          </Modal>
        )}
      </View>

      {/* Validation Error Message */}
      {hasError && (
        <ThemedText style={[styles.errorText, { color: BrandColors.beetRed.hex }]}>
          {error}
        </ThemedText>
      )}

      {/* Helper Text */}
      {!hasError && helperText && (
        <ThemedText style={[styles.helperText, { color: textMuted }]}>
          {helperText}
        </ThemedText>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    marginVertical: 4,
  },
  anchorContainer: {
    position: "relative",
    zIndex: 50,
  },
  fieldLabel: {
    marginBottom: 4,
    fontSize: 10,
    letterSpacing: 0.8,
  },
  triggerBase: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderWidth: 1,
    borderRadius: Platform.select({ android: MaterialShapes.small, default: 4 }),
    ...Platform.select({
      android: MaterialElevations.level1,
      default: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.02,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "150ms",
  } as any,
  triggerStandard: {
    height: 36,
    paddingHorizontal: 10,
  },
  triggerCompact: {
    height: 30,
    paddingHorizontal: 8,
  },
  triggerDisabled: {
    opacity: 0.5,
  },
  triggerContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    marginRight: 6,
  },
  leadingIcon: {
    marginRight: 6,
  },
  valueTextStandard: {
    fontSize: 13,
    fontFamily: Fonts.sans,
  },
  valueTextCompact: {
    fontSize: 12,
    fontWeight: "500",
    fontFamily: Fonts.sans,
  },
  menuCard: {
    minWidth: 180,
    borderRadius: Platform.select({ android: MaterialShapes.small, default: 6 }),
    borderWidth: 1,
    paddingVertical: 4,
    paddingHorizontal: 4,
    ...Platform.select({
      android: MaterialElevations.level3,
      default: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 8,
      },
    }),
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderBottomWidth: 1,
    marginBottom: 4,
  },
  searchInput: {
    flex: 1,
    fontSize: 12,
    paddingVertical: 2,
    paddingHorizontal: 4,
    fontFamily: Fonts.sans,
  },
  optionItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 4,
    borderWidth: 1,
    marginBottom: 2,
    ...Platform.select({
      web: {
        cursor: "pointer",
        transition: "background-color 0.12s ease, border-color 0.12s ease",
      } as any,
      default: {},
    }),
  },
  optionItemCompact: {
    paddingHorizontal: 6,
    paddingVertical: 4,
    marginBottom: 2,
  },
  optionItemHovered: {
    ...Platform.select({
      web: {
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
      } as any,
      default: {},
    }),
  },
  optionLabelContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    marginRight: 8,
  },
  optionLabel: {
    fontSize: 13,
    fontFamily: Fonts.sans,
  },
  optionLabelCompact: {
    fontSize: 12,
    fontFamily: Fonts.sans,
  },
  optionDesc: {
    fontSize: 11,
    fontFamily: Fonts.sans,
  },
  emptyOptionRow: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    alignItems: "center",
  },
  emptyOptionText: {
    fontSize: 12,
    fontStyle: "italic",
  },
  errorText: {
    fontSize: 10,
    lineHeight: 12,
    marginTop: 1,
    marginLeft: 2,
    fontWeight: "500",
  },
  helperText: {
    fontSize: 11,
    marginTop: 3,
    marginLeft: 2,
  },
});
